import React, { useMemo, useState } from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import WarningAmberRounded from '@mui/icons-material/WarningAmberRounded';
import TextTransformation from '../../utils/textTransformation';
import {
    CARD_CONTAINER_STYLE,
    CARD_STYLE,
    EMPTY_PRODUCT_STAT_CARDS,
    LABEL_STYLE,
    promotionLabels,
    STATUS_STYLES,
    VALUE_STYLE,
} from './kitchenProductsConstants';
import {
    buildProductStatCardsFromCounts,
    formatProductTypeLabel,
    toTitleCase,
} from './kitchenProductsListHelpers';

export function ProductListStatCards({ products = [], apiStatusCounts = null }) {
    const stats = useMemo(() => {
        if (apiStatusCounts && typeof apiStatusCounts === 'object' && !Array.isArray(apiStatusCounts)) {
            const getCount = (statusKey) => Number(apiStatusCounts[statusKey]) || 0;
            return buildProductStatCardsFromCounts(getCount);
        }

        if (!products.length) {
            return EMPTY_PRODUCT_STAT_CARDS.map((s) => ({ ...s }));
        }

        const statusCounts = products.reduce((acc, product) => {
            const status = product && product.status_type;
            if (!status) return acc;
            acc[status] = (acc[status] || 0) + 1;
            return acc;
        }, {});

        const getCount = (statusKey) => statusCounts[statusKey] || 0;
        return buildProductStatCardsFromCounts(getCount);
    }, [products, apiStatusCounts]);

    return (
        <div style={CARD_CONTAINER_STYLE}>
            {stats.map((stat, index) => {
                const statusStyle = STATUS_STYLES[stat.tone];
                const isActive = stat.value > 0 && statusStyle;
                const StatusIcon = statusStyle?.Icon;

                return (
                    <div
                        key={index}
                        style={{
                            ...CARD_STYLE,
                            borderLeft: isActive ? `3px solid ${statusStyle.color}` : `3px solid #e2e8f0`,
                        }}
                    >
                        <div
                            title={stat.label}
                            style={{
                                ...LABEL_STYLE,
                                color: isActive ? statusStyle.color : LABEL_STYLE.color,
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px',
                            }}
                        >
                            {isActive && StatusIcon ? (
                                <StatusIcon sx={{ fontSize: 16, color: statusStyle.color }} />
                            ) : null}
                            <span
                                style={{
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                    minWidth: 0,
                                    flex: 1,
                                }}
                            >
                                {stat.label}
                            </span>
                        </div>
                        <div
                            style={{
                                ...VALUE_STYLE,
                                color: isActive ? statusStyle.color : VALUE_STYLE.color,
                            }}
                        >
                            {stat.value.toLocaleString()}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

export const ProductRowActionMenu = ({ product, onEdit, onDelete }) => {
    const [anchorEl, setAnchorEl] = useState(null);

    const handleOpen = (e) => {
        e.stopPropagation();
        setAnchorEl(e.currentTarget);
    };
    const handleClose = () => setAnchorEl(null);

    return (
        <>
            <button
                type="button"
                onClick={handleOpen}
                style={{
                    background: 'none',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '20px',
                    color: '#64748b',
                    padding: '4px 8px',
                    borderRadius: '8px',
                    lineHeight: 1,
                    letterSpacing: '1px',
                }}
                title="Actions"
            >
                ⋮
            </button>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleClose}
                PaperProps={{
                    sx: {
                        boxShadow: '0 8px 24px rgba(15,23,42,0.16)',
                        minWidth: '140px',
                        borderRadius: '10px',
                        border: '1px solid #d7e0f2',
                    },
                }}
            >
                <MenuItem
                    onClick={() => {
                        onEdit(product);
                        handleClose();
                    }}
                    sx={{ fontSize: '13px', color: '#334155', py: '8px', fontWeight: 600 }}
                >
                    Edit
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        onDelete(product);
                        handleClose();
                    }}
                    sx={{ fontSize: '13px', color: '#dc2626', py: '8px', fontWeight: 600 }}
                >
                    Delete
                </MenuItem>
            </Menu>
        </>
    );
};

export const ProductCell = ({ product, toTitleCaseFn, onClick }) => {
    const qty = product.available_quantity || 0;
    const isOutOfStock = qty <= 0;
    const price = parseFloat(product.price || product.selling_price || 0).toFixed(2);

    return (
        <div
            onClick={onClick}
            onKeyDown={onClick ? (e) => e.key === 'Enter' && onClick() : undefined}
            role={onClick ? 'button' : undefined}
            tabIndex={onClick ? 0 : undefined}
            style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '2px 0',
                cursor: onClick ? 'pointer' : 'default',
            }}
        >
            <div>
                <div
                    style={{
                        fontFamily: 'Inter Medium',
                        fontWeight: '600',
                        fontSize: '13px',
                        color: '#1a1a2e',
                    }}
                >
                    {toTitleCaseFn(product.product_name || '')}
                </div>
                <div style={{ fontSize: '12px', marginTop: '3px', color: '#555' }}>
                    <span style={{ color: '#2e7d32', fontWeight: 800 }}>${price}</span>
                    <span style={{ color: '#ccc', margin: '0 5px' }}>|</span>
                    {isOutOfStock ? (
                        <span style={{ color: '#e53935' }}>Out of Stock</span>
                    ) : (
                        <span>QTY - {qty}</span>
                    )}
                </div>
                <div style={{ fontSize: '11px', color: '#9e9e9e', marginTop: '2px' }}>
                    SKU : {product.product_sku || 'N/A'}
                </div>
            </div>
        </div>
    );
};

function getStatusDisplay(status_type) {
    if (!status_type) return null;

    const normalized = String(status_type).toLowerCase();

    if (normalized === 'approved' || normalized === 'active') {
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#2e7d32',
                    fontSize: '13px',
                    fontFamily: 'Inter Bold',
                }}
            >
                <CheckCircleRounded sx={{ fontSize: 18, color: '#2e7d32' }} />
                {normalized === 'active' ? 'Active' : 'Approved'}
            </span>
        );
    }

    if (normalized === 'inactive') {
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#757575',
                    fontSize: '13px',
                    fontFamily: 'Inter Medium',
                }}
            >
                <CancelRounded sx={{ fontSize: 18, color: '#757575' }} />
                Inactive
            </span>
        );
    }

    if (normalized === 'rejected' || normalized === 'reject' || normalized.includes('reject')) {
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e53935',
                    fontSize: '13px',
                    fontFamily: 'Inter Medium',
                }}
            >
                <CancelRounded sx={{ fontSize: 18, color: '#e53935' }} />
                {TextTransformation(status_type)}
            </span>
        );
    }

    if (normalized.includes('pending') || normalized.includes('review')) {
        return (
            <span
                style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: '#e65100',
                    fontSize: '13px',
                    fontFamily: 'Inter Medium',
                }}
            >
                <WarningAmberRounded sx={{ fontSize: 18, color: '#e65100' }} />
                {TextTransformation(status_type)}
            </span>
        );
    }

    return (
        <span
            style={{
                color: '#e65100',
                fontSize: '13px',
                fontFamily: 'Inter Medium',
            }}
        >
            {TextTransformation(status_type)}
        </span>
    );
}

function getPromotion(product) {
    const keys = Object.keys(product.product_promotion || {});
    const activeKey = keys.find((k) => product.product_promotion[k]);
    if (!activeKey) return <span style={{ color: '#bbb' }}>—</span>;
    return (
        <span style={{ fontSize: '13px', color: '#2e7d32', fontWeight: 600 }}>
            {promotionLabels[activeKey] || TextTransformation(activeKey)}
        </span>
    );
}

const tableCellHeadSx = {
    padding: '11px 10px',
    fontFamily: 'Inter Bold',
    fontWeight: 700,
    fontSize: '11px',
    letterSpacing: '0.05em',
    textTransform: 'uppercase',
    color: '#64748b',
    borderBottom: '1px solid #d7e0f2',
    borderTop: 'none',
    whiteSpace: 'nowrap',
    backgroundColor: '#f8fafc',
};

const tableCellBodySx = {
    padding: '9px 10px',
    borderBottom: '1px solid #eef2f7',
    borderTop: 'none',
    borderLeft: 'none',
    borderRight: 'none',
    fontSize: '12px',
    color: '#334155',
    verticalAlign: 'middle',
};

export function KitchenProductsTable({
    filteredProducts,
    rowNumberOffset,
    showKitchenColumn,
    onProductRowClick,
    onEditProduct,
    onDeleteProduct,
}) {
    const [orderBy, setOrderBy] = useState(null);
    const [order, setOrder] = useState(null);

    const headCells = useMemo(
        () => [
            { id: 'sno', label: 'S.No', sortable: false },
            { id: 'product_name', label: 'Product', sortable: true },
            ...(showKitchenColumn ? [{ id: 'kitchen', label: 'Kitchen', sortable: true }] : []),
            { id: 'product_type', label: 'Product type', sortable: true },
            { id: 'product_category', label: 'Category', sortable: true },
            { id: 'promotion', label: 'Promotion', sortable: true },
            { id: 'status', label: 'Status', sortable: true },
            { id: 'action', label: 'Action', sortable: false },
        ],
        [showKitchenColumn]
    );

    const sortedProducts = useMemo(() => {
        const list = filteredProducts || [];
        if (order == null || orderBy == null) return list;

        const getPromotionSortValue = (product) => {
            const keys = Object.keys(product.product_promotion || {});
            const activeKey = keys.find((k) => product.product_promotion[k]);
            return activeKey ? promotionLabels[activeKey] || activeKey : '';
        };

        return [...list].sort((a, b) => {
            let aVal;
            let bVal;
            switch (orderBy) {
                case 'product_name':
                    aVal = (a.product_name || '').toLowerCase();
                    bVal = (b.product_name || '').toLowerCase();
                    break;
                case 'kitchen':
                    aVal = (a.kitchen || '').toLowerCase();
                    bVal = (b.kitchen || '').toLowerCase();
                    break;
                case 'product_type':
                    aVal = (a.product_type || '').toLowerCase();
                    bVal = (b.product_type || '').toLowerCase();
                    break;
                case 'product_category':
                    aVal = (a.product_category || '').toLowerCase();
                    bVal = (b.product_category || '').toLowerCase();
                    break;
                case 'promotion':
                    aVal = getPromotionSortValue(a).toLowerCase();
                    bVal = getPromotionSortValue(b).toLowerCase();
                    break;
                case 'status':
                    aVal = (a.status_type || '').toLowerCase();
                    bVal = (b.status_type || '').toLowerCase();
                    break;
                default:
                    return 0;
            }
            if (aVal < bVal) return order === 'asc' ? -1 : 1;
            if (aVal > bVal) return order === 'asc' ? 1 : -1;
            return 0;
        });
    }, [filteredProducts, orderBy, order]);

    const handleRequestSort = (event, property) => {
        if (orderBy !== property) {
            setOrderBy(property);
            setOrder('asc');
            return;
        }
        if (order === 'asc') {
            setOrder('desc');
            return;
        }
        if (order === 'desc') {
            setOrderBy(null);
            setOrder(null);
        }
    };

    if (!filteredProducts || filteredProducts.length === 0) {
        return (
            <div
                style={{
                    padding: '52px',
                    textAlign: 'center',
                    color: '#64748b',
                    fontSize: '14px',
                    backgroundColor: 'white',
                    border: '1px solid #d7e0f2',
                    borderRadius: '10px',
                }}
            >
                No products available.
            </div>
        );
    }

    const currentItems = sortedProducts;
    const startIndex = rowNumberOffset ?? 0;

    return (
        <TableContainer
            component={Paper}
            elevation={0}
            sx={{
                width: '100%',
                overflowX: 'auto',
                WebkitOverflowScrolling: 'touch',
                backgroundColor: 'white',
                border: '1px solid #d7e0f2',
                borderRadius: '12px',
                boxShadow: '0 4px 16px rgba(15,23,42,0.08)',
            }}
        >
            <Table sx={{ minWidth: 700 }} size="small" aria-label="products data table">
                <TableHead>
                    <TableRow>
                        {headCells.map((cell) => (
                            <TableCell
                                key={cell.id}
                                align={cell.id === 'sno' ? 'center' : 'left'}
                                sx={{
                                    ...tableCellHeadSx,
                                    ...(cell.id === 'sno' ? { textAlign: 'center' } : {}),
                                }}
                            >
                                {cell.sortable ? (
                                    <TableSortLabel
                                        active={orderBy === cell.id}
                                        direction={orderBy === cell.id && order ? order : 'asc'}
                                        onClick={(e) => handleRequestSort(e, cell.id)}
                                    >
                                        {cell.label}
                                    </TableSortLabel>
                                ) : (
                                    cell.label
                                )}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {currentItems.map((product, index) => (
                        <TableRow
                            key={product.product_id ?? product.id ?? index}
                            sx={{
                                backgroundColor: index % 2 === 0 ? 'white' : '#f8fafc',
                                transition: 'background-color 0.15s ease',
                                '&:hover': { backgroundColor: '#fff7ed' },
                            }}
                            hover
                        >
                            <TableCell
                                sx={{
                                    ...tableCellBodySx,
                                    width: 60,
                                    color: '#94a3b8',
                                    fontSize: '13px',
                                    textAlign: 'center',
                                }}
                            >
                                {startIndex + index + 1}
                            </TableCell>
                            <TableCell sx={{ ...tableCellBodySx, minWidth: 180 }}>
                                <ProductCell
                                    product={product}
                                    toTitleCaseFn={toTitleCase}
                                    onClick={() => onProductRowClick?.(product)}
                                />
                            </TableCell>
                            {showKitchenColumn && (
                                <TableCell sx={tableCellBodySx}>{toTitleCase(product.kitchen || '—')}</TableCell>
                            )}
                            <TableCell sx={tableCellBodySx}>
                                {formatProductTypeLabel(product.product_type || 'N/A')}
                            </TableCell>
                            <TableCell sx={tableCellBodySx}>
                                {toTitleCase(product.product_category || 'N/A')}
                            </TableCell>
                            <TableCell
                                sx={{
                                    ...tableCellBodySx,
                                    maxWidth: 100,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                }}
                            >
                                {getPromotion(product)}
                            </TableCell>
                            <TableCell sx={tableCellBodySx}>{getStatusDisplay(product.status_type)}</TableCell>
                            <TableCell sx={{ ...tableCellBodySx, textAlign: 'center', width: 60 }}>
                                <ProductRowActionMenu
                                    product={product}
                                    onEdit={onEditProduct}
                                    onDelete={onDeleteProduct}
                                />
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
}
